import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Printer, QrCode, BarChart3, Minus, Plus } from 'lucide-react';
import JsBarcode from 'jsbarcode';
import { QRCodeCanvas } from 'qrcode.react';
import jsPDF from 'jspdf';

// Configurations de taille
const SIZES = {
  small: { label: 'Petit', width: 40, height: 25, fontSize: 6, codeHeight: 15, gap: 2 },
  medium: { label: 'Moyen', width: 55, height: 35, fontSize: 7, codeHeight: 20, gap: 3 },
  large: { label: 'Grand', width: 75, height: 50, fontSize: 9, codeHeight: 28, gap: 4 },
};

const ProductLabelGenerator = ({ product, onClose }) => {
  const [codeType, setCodeType] = useState('barcode'); // 'barcode' ou 'qrcode'
  const [size, setSize] = useState('medium');
  const [copies, setCopies] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [showFields, setShowFields] = useState({ code: true, nom: true, description: true, prix: true });

  const toggleField = (field) => setShowFields(prev => ({ ...prev, [field]: !prev[field] }));
  const barcodeRef = useRef(null);
  const qrRef = useRef(null);

  // Code produit : utilise code_produit ou génère un code basé sur l'ID
  const productCode = product.code_produit || `PRD${String(product.id).padStart(6, '0')}`;

  // Générer le code-barres SVG pour l'aperçu
  useEffect(() => {
    if (codeType === 'barcode' && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, productCode, {
          format: 'CODE128',
          width: 1.5,
          height: 40,
          displayValue: true,
          fontSize: 10,
          margin: 2,
          textMargin: 2,
        });
      } catch (e) {
        console.error('Erreur code-barres:', e);
      }
    }
  }, [codeType, productCode]);

  // Calcul du nombre d'étiquettes par page A4
  const getLabelsPerPage = useCallback(() => {
    const s = SIZES[size];
    const pageW = 210; // A4 largeur mm
    const pageH = 297; // A4 hauteur mm
    const marginX = 5;
    const marginY = 5;
    const cols = Math.floor((pageW - 2 * marginX) / (s.width + s.gap));
    const rows = Math.floor((pageH - 2 * marginY) / (s.height + s.gap));
    return { cols, rows, total: cols * rows };
  }, [size]);

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const s = SIZES[size];
      const { cols, rows } = getLabelsPerPage();
      const marginX = 5;
      const marginY = 5;

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Générer l'image du code
      let codeDataUrl;
      if (codeType === 'barcode') {
        const canvas = document.createElement('canvas');
        JsBarcode(canvas, productCode, {
          format: 'CODE128',
          width: 2,
          height: 60,
          displayValue: false,
          margin: 0,
        });
        codeDataUrl = canvas.toDataURL('image/png');
      } else {
        // QR code — render dans un canvas temporaire
        const qrCanvas = document.querySelector('#qr-hidden-canvas canvas');
        if (qrCanvas) {
          codeDataUrl = qrCanvas.toDataURL('image/png');
        }
      }

      if (!codeDataUrl) {
        throw new Error('Impossible de générer le code');
      }

      let labelCount = 0;
      const totalLabels = copies;

      for (let i = 0; i < totalLabels; i++) {
        if (labelCount > 0 && labelCount % (cols * rows) === 0) {
          pdf.addPage();
        }

        const pagePos = labelCount % (cols * rows);
        const col = pagePos % cols;
        const row = Math.floor(pagePos / cols);

        const x = marginX + col * (s.width + s.gap);
        const y = marginY + row * (s.height + s.gap);

        // Bordure de l'étiquette (pointillée)
        pdf.setDrawColor(200, 200, 200);
        pdf.setLineDashPattern([1, 1], 0);
        pdf.rect(x, y, s.width, s.height);

        const padding = 1.5;
        const contentX = x + padding;
        const contentW = s.width - 2 * padding;

        // Code (barres ou QR)
        let textY = y + padding;
        if (showFields.code) {
          const codeW = codeType === 'qrcode' ? s.codeHeight : contentW - 4;
          const codeX = contentX + (contentW - codeW) / 2;
          const codeY = y + padding;
          const codeH = codeType === 'qrcode' ? s.codeHeight : s.codeHeight * 0.6;
          pdf.addImage(codeDataUrl, 'PNG', codeX, codeY, codeW, codeH);
          textY = codeY + codeH + 3.5;
        } else {
          textY = y + padding + 2;
        }

        // Nom du produit
        if (showFields.nom) {
          pdf.setFontSize(s.fontSize);
          pdf.setFont('helvetica', 'bold');
          const nameText = truncateText(product.nom, s.fontSize, contentW, pdf);
          pdf.text(nameText, contentX + contentW / 2, textY, { align: 'center' });
          textY += s.fontSize * 0.4 + 0.8;
        }

        // Description (si espace disponible)
        if (showFields.description && product.description && textY + s.fontSize * 0.3 < y + s.height - s.fontSize * 0.4 - 1) {
          pdf.setFontSize(s.fontSize - 1);
          pdf.setFont('helvetica', 'normal');
          const descText = truncateText(product.description, s.fontSize - 1, contentW, pdf);
          pdf.text(descText, contentX + contentW / 2, textY, { align: 'center' });
          textY += (s.fontSize - 1) * 0.4 + 0.8;
        }

        // Prix de vente
        if (showFields.prix) {
          pdf.setFontSize(s.fontSize + 1);
          pdf.setFont('helvetica', 'bold');
          const priceText = `${parseInt(product.prix_vente).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} FCFA`;
          const priceY = Math.min(textY, y + s.height - padding - 0.5);
          pdf.text(priceText, contentX + contentW / 2, priceY, { align: 'center' });
        }

        labelCount++;
      }

      // Ouvrir le PDF dans un nouvel onglet
      const pdfBlob = pdf.output('blob');
      const url = URL.createObjectURL(pdfBlob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const truncateText = (text, fontSize, maxWidth, pdf) => {
    if (!text) return '';
    pdf.setFontSize(fontSize);
    let t = text;
    while (pdf.getTextWidth(t) > maxWidth && t.length > 3) {
      t = t.slice(0, -1);
    }
    if (t.length < text.length) t = t.slice(0, -1) + '…';
    return t;
  };

  const { cols, rows, total: labelsPerPage } = getLabelsPerPage();
  const totalPages = Math.ceil(copies / labelsPerPage);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-5 border-b flex justify-between items-center bg-gradient-to-r from-indigo-50 to-purple-50">
          <div>
            <h2 className="text-lg font-bold text-gray-800">Générer les étiquettes</h2>
            <p className="text-sm text-gray-500 mt-0.5">{product.nom}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-full transition">
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Type de code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de code</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCodeType('barcode')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition font-medium text-sm ${
                  codeType === 'barcode'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <BarChart3 size={18} /> Code-barres
              </button>
              <button
                type="button"
                onClick={() => setCodeType('qrcode')}
                className={`flex items-center justify-center gap-2 py-3 rounded-xl border-2 transition font-medium text-sm ${
                  codeType === 'qrcode'
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                    : 'border-gray-200 text-gray-600 hover:border-gray-300'
                }`}
              >
                <QrCode size={18} /> QR Code
              </button>
            </div>
          </div>

          {/* Taille */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Taille de l'étiquette</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(SIZES).map(([key, s]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSize(key)}
                  className={`py-3 rounded-xl border-2 transition text-sm font-medium ${
                    size === key
                      ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div>{s.label}</div>
                  <div className="text-xs opacity-70">{s.width}×{s.height}mm</div>
                </button>
              ))}
            </div>
          </div>

          {/* Données à afficher */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Informations à afficher</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { key: 'code', label: 'Code (barres / QR)' },
                { key: 'nom', label: 'Nom du produit' },
                { key: 'description', label: 'Description' },
                { key: 'prix', label: 'Prix de vente' },
              ].map(({ key, label }) => (
                <label
                  key={key}
                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer transition text-sm ${
                    showFields[key]
                      ? 'border-indigo-300 bg-indigo-50 text-indigo-700'
                      : 'border-gray-200 bg-white text-gray-500'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={showFields[key]}
                    onChange={() => toggleField(key)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>

          {/* Nombre d'exemplaires */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre d'exemplaires</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setCopies(c => Math.max(1, c - 1))}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition"
              >
                <Minus size={16} />
              </button>
              <input
                type="number"
                min="1"
                max="500"
                value={copies}
                onChange={(e) => setCopies(Math.max(1, Math.min(500, parseInt(e.target.value) || 1)))}
                className="w-24 text-center border rounded-lg px-3 py-2 text-lg font-bold focus:ring-2 focus:ring-indigo-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setCopies(c => Math.min(500, c + 1))}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-300 hover:bg-gray-100 transition"
              >
                <Plus size={16} />
              </button>
              <div className="ml-2 text-xs text-gray-400">
                {labelsPerPage} / page • {totalPages} page{totalPages > 1 ? 's' : ''}
              </div>
            </div>
            {/* Raccourcis */}
            <div className="flex gap-2 mt-2">
              {[1, 10, 20, 50, 100].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setCopies(n)}
                  className={`px-3 py-1 text-xs rounded-full border transition ${
                    copies === n ? 'bg-indigo-100 border-indigo-300 text-indigo-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {/* Aperçu */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Aperçu</label>
            <div className="border rounded-xl p-4 bg-gray-50 flex justify-center">
              <div className="bg-white border border-dashed border-gray-300 rounded-lg p-3 inline-flex flex-col items-center" style={{ minWidth: '180px' }}>
                {showFields.code && (
                  codeType === 'barcode' ? (
                    <svg ref={barcodeRef} className="w-full max-w-[200px]"></svg>
                  ) : (
                    <QRCodeCanvas value={productCode} size={100} level="M" />
                  )
                )}
                <div className={`${showFields.code ? 'mt-2' : ''} text-center`}>
                  {showFields.nom && (
                    <div className="font-bold text-sm text-gray-800">{product.nom}</div>
                  )}
                  {showFields.description && product.description && (
                    <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{product.description}</div>
                  )}
                  {showFields.prix && (
                    <div className="font-bold text-sm text-gray-900 mt-1">
                      {parseInt(product.prix_vente).toLocaleString()} FCFA
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* QR canvas caché pour la génération PDF */}
          <div id="qr-hidden-canvas" style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
            <QRCodeCanvas value={productCode} size={200} level="M" ref={qrRef} />
          </div>

          {/* Code produit */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm">
            <span className="text-gray-500">Code :</span>
            <span className="font-mono font-bold text-gray-800">{productCode}</span>
            {!product.code_produit && (
              <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">Auto-généré</span>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t bg-gray-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 rounded-xl text-gray-600 font-medium hover:bg-gray-100 transition"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={generatePDF}
            disabled={generating}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-indigo-600 text-white rounded-xl font-medium hover:bg-indigo-700 disabled:opacity-50 transition"
          >
            <Printer size={16} />
            {generating ? 'Génération...' : 'Générer le PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductLabelGenerator;
